import type { PipelineStepContext, PipelineStepResult } from '..';
import { PipelineBaseStep, ServiceMode, usePaymentFlowStore } from '..';

export class ValidateCartStep extends PipelineBaseStep {
  readonly name = 'ValidateCartStep';

  async execute(pipelineContext: PipelineStepContext): Promise<PipelineStepResult> {
    usePaymentFlowStore.getState().setCurrentState('validatingCart');

    this.logger.log('Validating cart', {
      cartTotal: pipelineContext.cartTotal,
      cartSubTotal: pipelineContext.cartSubTotal,
      entries: pipelineContext.cartEntries.length,
      serverOrderStatus: pipelineContext.serverOrderStatus,
    });

    // 1. Check if cart has items
    if (!pipelineContext.cartEntries || pipelineContext.cartEntries.length === 0) {
      return this.error(
        'CART_EMPTY',
        'Cart is empty',
        'Please add items to your cart before proceeding'
        // No retryFromStep - not retryable, user must add items
      );
    }

    // 2. Check for unavailable items
    if (pipelineContext.unavailableCartEntries) {
      return this.error(
        'CART_HAS_UNAVAILABLE_ITEMS',
        `Cart contains unavailable item(s)`,
        'Some items in your cart are no longer available. Please review your cart; Error code: (Unavailable Items)'
        // No retryFromStep - not retryable, user must remove items
      );
    }

    // 3. Check if cart total is negative
    if (pipelineContext.cartTotal < 0 || pipelineContext.cartSubTotal < 0) {
      return this.error(
        'CART_TOTAL_NEGATIVE',
        `Cart total is negative. Total: ${pipelineContext.cartTotal}, Subtotal: ${pipelineContext.cartSubTotal}`,
        'There was an error calculating your cart total. Please try again. Error code: (Negative Order)',
        'ValidateCartStep' // retryFromStep - can retry after cart updates
      );
    }

    // 4. Check maximum cart limit (if configured)
    if (pipelineContext.maxCartLimit && pipelineContext.cartTotal > pipelineContext.maxCartLimit) {
      const maxDollars = (pipelineContext.maxCartLimit / 100).toFixed(2);
      return this.error(
        'CART_OVER_LIMIT',
        `Cart total ${pipelineContext.cartTotal} exceeds maximum limit of ${pipelineContext.maxCartLimit}`,
        `Your cart total exceeds the maximum allowed amount of $${maxDollars}; Error code: (Max Cart Limit)`
        // No retryFromStep - not retryable, user must adjust cart
      );
    }

    // 5. Check minimum delivery amount (for delivery orders only)
    const isDelivery = pipelineContext.serviceMode === ServiceMode.DELIVERY;
    if (
      isDelivery &&
      pipelineContext.minDeliveryAmount &&
      pipelineContext.cartSubTotal < pipelineContext.minDeliveryAmount
    ) {
      const minDollars = (pipelineContext.minDeliveryAmount / 100).toFixed(2);
      return this.error(
        'CART_BELOW_MINIMUM',
        `Cart subtotal ${pipelineContext.cartSubTotal} is below minimum delivery amount of ${pipelineContext.minDeliveryAmount}`,
        `Delivery orders must be at least $${minDollars}; Error code: (Min Cart Limit)`
        // No retryFromStep - not retryable, user must add items
      );
    }

    // All validations passed
    this.logger.log('Cart validation successful');
    usePaymentFlowStore.getState().setCurrentState('idle');
    return this.success();
  }
}
